import React, { useContext, useEffect, useRef, useState } from 'react';
import Helmet from 'react-helmet';
import { useLocation, useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';
import FrameFacade from './components/FrameFacade';
import JustificationContainer from '~/widget/components/JustificationContainer';
import HeightContext from '~/widget/context/HeightContext';
import useDebouncedCallback from '~/widget/hooks/useDebouncedCallback';
import useLocale from '~/widget/hooks/useLocale';
import useWidgetContext from '~/widget/hooks/useWidgetContext';
import { layoutSelector, widgetState } from '~/widget/state/widgetState';
import useMessaging from '~/widget/hooks/useMessaging';
import useTracking from '~/widget/hooks/useTracking';

const AppPage: React.FC = () => {
  const location = useLocation();
  const locale = useLocale();
  const doTrack = useTracking();

  const { headerRef, footerRef } = useContext(HeightContext);
  const context = useWidgetContext();

  const [minHeight, setMinHeight] = useState<number>(300);
  const [state, setWidgetState] = useRecoilState(widgetState);
  const [layout, setLayout] = useRecoilState(layoutSelector);

  const mainRef = useRef<HTMLDivElement | null>(null);
  const { slug } = useParams();

  // Only set loading state on mount
  useEffect(() => {
    setWidgetState((prev) => ({ ...prev, loading: true }));
  }, []);

  // Update title
  useEffect(() => {
    if (typeof slug === 'string' && context.apps[slug]) {
      doTrack({ name: 'app_shown', slug });

      setTimeout(() => {
        setWidgetState((prev) => ({
          ...prev,
          title: context.apps[slug].title[locale],
        }));
      }, 0);
    }

    return () => {
      // Reset title when we navigate away
      setWidgetState((prev) => ({
        ...prev,
        title: null,
      }));
    };
    // Update title when locale changes
  }, [locale, slug]);

  useMessaging((message) => {
    switch (message.type) {
      case 'track': {
        const messageName = message.payload?.name ?? 'unknown';
        const params = message.payload?.params ?? {};
        const { conversion, featureFlags = {}, ...properties } = params;

        doTrack({
          name: 'app_event',
          slug: slug ?? 'unknown',
          is_conversion: conversion === true,
          messageName,
          properties,
          featureFlags,
        });
        break;
      }

      default:
        break;
    }
  });

  useEffect(() => {
    if (layout !== 'wide') {
      setLayout('wide');
    }
  }, [layout, location.pathname]);

  // Subtract a few px so the layout doesn't overflow and trigger a scrollbar.
  // getBoundingClientRect() can be fractional; rounding when summing header + content + footer
  // can exceed the viewport by 2–4px. Divider(s) and borders add 1–2px. Hence the correction.
  // const HEIGHT_OVERFLOW_CORRECTION_PX = 4;
  const HEIGHT_OVERFLOW_CORRECTION_PX = 4;

  const onResize = useDebouncedCallback(() => {
    const headerBound = headerRef.current?.getBoundingClientRect();
    const footerBound = footerRef.current?.getBoundingClientRect();

    const contentHeight = (() => {
      if (headerBound && footerBound) {
        return footerBound.top - headerBound.top - headerBound.height;
      }

      // Without header and footer (bndl_wgt_single_app links) the main element is the content area
      return mainRef.current?.getBoundingClientRect().height ?? null;
    })();

    if (contentHeight === null) return;

    setMinHeight(Math.floor(contentHeight - HEIGHT_OVERFLOW_CORRECTION_PX));
  }, 50);

  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (typeof slug !== 'string') return <div>TODO: NOT FOUND</div>;
  const app = context.apps[slug];
  if (!app) return <div>TODO: NOT FOUND</div>;

  return (
    <Main ref={mainRef}>
      <Helmet>
        <title>{state.title}</title>
      </Helmet>
      <Container justification="start" align="center">
        <FrameContainer>
          <FrameFacade app={app} minHeight={minHeight} />
        </FrameContainer>
      </Container>
    </Main>
  );
};

const Container = styled(JustificationContainer)`
  height: 100%;
`;

const Main = styled.main`
  padding: 0;
  height: 100%;
`;

const FrameContainer = styled.div`
  width: 100%;
  height: 100%;
  min-width: 100%;
  margin: 0 auto;
`;

export default AppPage;
